import { useCallback, useEffect, useRef, useState } from 'react'
import type { MazePuzzle } from '../../types'
import { canMove } from '../../lib/maze/generator'

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL       = 32          // SVG user-unit size per cell
const WALL_THIN  = 1.5         // inner wall stroke width
const WALL_THICK = 2.5         // outer / box-boundary stroke width
const PATH_W     = CELL * 0.42 // user path stroke width
const SOL_W      = CELL * 0.35 // solution stroke width
const PAD        = 18          // extra viewBox padding top & bottom (for markers)

// ─── Types ────────────────────────────────────────────────────────────────────

type Pos = [number, number] // [row, col]

interface MazeCanvasProps {
  maze:         MazePuzzle
  showSolution: boolean
  onSolved:     () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a mouse or touch client position into SVG user coordinates */
function clientToSVG(svg: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
  const pt  = svg.createSVGPoint()
  pt.x      = clientX
  pt.y      = clientY
  const ctm = svg.getScreenCTM()
  if (!ctm) return { x: 0, y: 0 }
  const t = pt.matrixTransform(ctm.inverse())
  return { x: t.x, y: t.y }
}

/** Map SVG coordinates → maze cell, clamped to valid range */
function xyToCell(x: number, y: number, rows: number, cols: number): Pos {
  return [
    Math.max(0, Math.min(rows - 1, Math.floor(y / CELL))),
    Math.max(0, Math.min(cols - 1, Math.floor(x / CELL))),
  ]
}

function posEq(a: Pos, b: Pos) { return a[0] === b[0] && a[1] === b[1] }

// ─── Wall segments ────────────────────────────────────────────────────────────

function WallLines({ maze }: { maze: MazePuzzle }) {
  const { cells, rows, cols } = maze
  const lines: { x1: number; y1: number; x2: number; y2: number; outer: boolean }[] = []

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = c * CELL, y = r * CELL

      // Top wall (covers all rows; avoids duplicate with cell above's bottom)
      if (cells[r][c].top)
        lines.push({ x1: x, y1: y, x2: x + CELL, y2: y, outer: r === 0 })

      // Left wall (covers all cols; avoids duplicate with cell left's right)
      if (cells[r][c].left)
        lines.push({ x1: x, y1: y, x2: x, y2: y + CELL, outer: c === 0 })

      // Right wall — only rightmost column
      if (c === cols - 1 && cells[r][c].right)
        lines.push({ x1: x + CELL, y1: y, x2: x + CELL, y2: y + CELL, outer: true })

      // Bottom wall — only bottom row
      if (r === rows - 1 && cells[r][c].bottom)
        lines.push({ x1: x, y1: y + CELL, x2: x + CELL, y2: y + CELL, outer: true })
    }
  }

  return (
    <>
      {lines.map(({ x1, y1, x2, y2, outer }, i) => (
        <line
          key={i}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke="#2D2D2D"
          strokeWidth={outer ? WALL_THICK : WALL_THIN}
          strokeLinecap="square"
        />
      ))}
    </>
  )
}

// ─── Path polyline ────────────────────────────────────────────────────────────

function PathLine({ path, color, width, opacity = 1 }: {
  path:    Pos[]
  color:   string
  width:   number
  opacity?: number
}) {
  if (path.length < 1) return null
  const pts = path
    .map(([r, c]) => `${c * CELL + CELL / 2},${r * CELL + CELL / 2}`)
    .join(' ')

  return (
    <polyline
      points={pts}
      fill="none"
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={opacity}
    />
  )
}

// ─── Canvas ───────────────────────────────────────────────────────────────────

export default function MazeCanvas({ maze, showSolution, onSolved }: MazeCanvasProps) {
  const { rows, cols, cells, entryCol, exitCol, solution } = maze
  const svgRef = useRef<SVGSVGElement>(null)

  const [path, setPath]       = useState<Pos[]>([])
  const [drawing, setDrawing] = useState(false)
  const [solved, setSolved]   = useState(false)

  const vbWidth  = cols * CELL
  const vbHeight = rows * CELL

  // ── Path logic ────────────────────────────────────────────────────────

  const handleCell = useCallback((cell: Pos) => {
    setPath((prev) => {
      const last = prev[prev.length - 1]
      const secondLast = prev[prev.length - 2]

      // Backtrack if user moves to second-to-last cell
      if (secondLast && posEq(cell, secondLast)) {
        return prev.slice(0, -1)
      }

      // Try to extend the path
      if (last) {
        if (posEq(cell, last)) return prev          // same cell — no change
        if (!canMove(cells, last, cell)) return prev // wall in the way
        return [...prev, cell]
      }

      // No path yet — start from this cell
      return [cell]
    })
  }, [cells])

  // Detect success whenever path changes
  useEffect(() => {
    if (solved) return
    const last = path[path.length - 1]
    if (last && last[0] === rows - 1 && last[1] === exitCol) {
      setSolved(true)
      onSolved()
    }
  }, [path, rows, exitCol, solved, onSolved])

  // Reset solved state if maze changes
  useEffect(() => {
    setPath([])
    setSolved(false)
    setDrawing(false)
  }, [maze])

  // ── Pointer → cell helper ─────────────────────────────────────────────

  function pointerCell(clientX: number, clientY: number): Pos | null {
    if (!svgRef.current) return null
    const { x, y } = clientToSVG(svgRef.current, clientX, clientY)
    if (x < 0 || y < 0 || x > vbWidth || y > vbHeight) return null
    return xyToCell(x, y, rows, cols)
  }

  // ── Mouse events ──────────────────────────────────────────────────────

  function onMouseDown(e: React.MouseEvent<SVGSVGElement>) {
    if (solved) return
    const cell = pointerCell(e.clientX, e.clientY)
    if (cell) { setDrawing(true); handleCell(cell) }
  }

  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!drawing || solved) return
    const cell = pointerCell(e.clientX, e.clientY)
    if (cell) handleCell(cell)
  }

  function onMouseUp() { setDrawing(false) }

  // ── Touch events (non-passive to allow preventDefault) ────────────────

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return

    function onTouchStart(e: TouchEvent) {
      if (solved) return
      e.preventDefault()
      const t = e.touches[0]
      const cell = pointerCell(t.clientX, t.clientY)
      if (cell) { setDrawing(true); handleCell(cell) }
    }

    function onTouchMove(e: TouchEvent) {
      if (!drawing || solved) return
      e.preventDefault()
      const t = e.touches[0]
      const cell = pointerCell(t.clientX, t.clientY)
      if (cell) handleCell(cell)
    }

    function onTouchEnd() { setDrawing(false) }

    svg.addEventListener('touchstart', onTouchStart, { passive: false })
    svg.addEventListener('touchmove',  onTouchMove,  { passive: false })
    svg.addEventListener('touchend',   onTouchEnd)
    return () => {
      svg.removeEventListener('touchstart', onTouchStart)
      svg.removeEventListener('touchmove',  onTouchMove)
      svg.removeEventListener('touchend',   onTouchEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing, solved, handleCell, rows, cols, cells])

  // ── SVG viewBox includes top/bottom padding for markers ───────────────

  const viewBox = `0 ${-PAD} ${vbWidth} ${vbHeight + PAD * 2}`

  const entryX = entryCol * CELL + CELL / 2
  const exitX  = exitCol  * CELL + CELL / 2

  return (
    <div style={{ width: '100%', maxWidth: Math.min(vbWidth + 4, 520) }} className="mx-auto">
      <svg
        ref={svgRef}
        viewBox={viewBox}
        width="100%"
        style={{ display: 'block', cursor: solved ? 'default' : 'crosshair', userSelect: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        aria-label="Maze puzzle"
        role="img"
      >
        {/* ── Entry marker (triangle pointing down into maze) ── */}
        <polygon
          points={`${entryX - 6},${-PAD + 2} ${entryX + 6},${-PAD + 2} ${entryX},${-2}`}
          fill="#4ECDC4"
        />
        <text
          x={entryX} y={-PAD + 1}
          textAnchor="middle"
          fontSize={9}
          fontFamily="Nunito, sans-serif"
          fontWeight={900}
          fill="#4ECDC4"
          dominantBaseline="auto"
        >
          START
        </text>

        {/* ── Exit marker (triangle pointing away from maze) ── */}
        <polygon
          points={`${exitX - 6},${vbHeight + 2} ${exitX + 6},${vbHeight + 2} ${exitX},${vbHeight + PAD - 2}`}
          fill="#FF6B6B"
        />
        <text
          x={exitX} y={vbHeight + PAD}
          textAnchor="middle"
          fontSize={9}
          fontFamily="Nunito, sans-serif"
          fontWeight={900}
          fill="#FF6B6B"
          dominantBaseline="auto"
        >
          EXIT
        </text>

        {/* ── User path ── */}
        <PathLine
          path={path}
          color={solved ? '#4ECDC4' : '#FF6B6B'}
          width={PATH_W}
          opacity={0.75}
        />

        {/* ── Solution overlay ── */}
        {showSolution && (
          <PathLine
            path={solution}
            color="#7B2FBE"
            width={SOL_W}
            opacity={0.5}
          />
        )}

        {/* ── Walls (rendered on top of paths so they always show) ── */}
        <WallLines maze={maze} />
      </svg>

      {/* ── Controls ── */}
      <div className="flex gap-2 justify-center mt-3">
        <button
          onClick={() => { setPath([]); setSolved(false) }}
          disabled={path.length === 0}
          className="btn-secondary text-sm px-4 py-2 disabled:opacity-40"
        >
          ↺ Clear path
        </button>
      </div>
    </div>
  )
}
